'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useUser } from '@stackframe/stack'
import { useState, useEffect } from 'react'
import { BsTrophy, BsBoxArrowInRight } from 'react-icons/bs'
import { StreakBadge } from '@/components/gamification'

interface DailyChallenge {
  id: string
  name: string
  type: 'BRACKET_8' | 'BRACKET_16' | 'DAILY_VOTE' | 'RANKING'
  entityImages: string[]
}

export function TopBar() {
  const user = useUser()
  const [challenge, setChallenge] = useState<DailyChallenge | null>(null)
  const [loading, setLoading] = useState(true)
  const [streak, setStreak] = useState<{
    currentStreak: number
    longestStreak: number
    activeToday: boolean
  } | null>(null)

  useEffect(() => {
    async function fetchChallenge() {
      try {
        const res = await fetch('/api/challenges/today')
        if (res.ok) {
          const data = await res.json()
          setChallenge(data.challenge)
        }
      } catch (error) {
        console.error('Failed to fetch daily challenge:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchChallenge()
  }, [])

  useEffect(() => {
    if (!user) {
      setStreak(null)
      return
    }
    let cancelled = false
    fetch('/api/user/streak')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.streak) setStreak(data.streak)
      })
      .catch(() => {
        // ignore
      })
    return () => {
      cancelled = true
    }
  }, [user])

  return (
    <div className="bg-gradient-to-r from-[#1E4289] to-[#2563eb] text-white py-2.5 px-5 h-[48px]">
      <div className="flex items-center justify-between max-w-7xl mx-auto gap-3">
        {/* Daily Challenge */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {loading ? (
            <div className="flex items-center gap-3 text-base text-white/70">
              <span>Loading challenge...</span>
            </div>
          ) : challenge ? (
            <Link
              href={challenge.type === 'RANKING' ? `/challenges/${challenge.id}/rank` : `/challenges/${challenge.id}`}
              className="flex items-center gap-3 text-base hover:text-[#FEDB1A] transition-colors group min-w-0"
            >
              <span className="text-[#FEDB1A] font-bold">DAILY CHALLENGE:</span>
              <span className="text-[#FEDB1A] font-semibold truncate">{challenge.name}</span>

              {/* Entity Images */}
              {challenge.entityImages && challenge.entityImages.length > 0 && (
                <div className="hidden sm:flex items-center -space-x-2 flex-shrink-0">
                  {challenge.entityImages.slice(0, 3).map((img, idx) => (
                    <div
                      key={idx}
                      className="relative w-9 h-9 rounded-full border-2 border-[#1E4289] overflow-hidden bg-slate-700"
                    >
                      <Image
                        src={img}
                        alt=""
                        fill
                        className="object-cover"
                        unoptimized
                      />
                    </div>
                  ))}
                </div>
              )}

              <span className="text-white/70 text-sm hidden lg:inline flex-shrink-0">→ Vote Now!</span>
            </Link>
          ) : (
            <div className="flex items-center gap-3 text-base text-white/70">
              <BsTrophy className="text-[#FEDB1A] flex-shrink-0 text-lg" />
              <span>No active challenge today</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {user && streak && (
            <Link href="/dashboard/user" className="hover:opacity-90 transition-opacity">
              <StreakBadge
                currentStreak={streak.currentStreak}
                longestStreak={streak.longestStreak}
                activeToday={streak.activeToday}
                compact
              />
            </Link>
          )}

          {/* Login Button (only show if not logged in) */}
          {!user && (
            <Link
              href="/auth/signin"
              className="flex items-center gap-2 px-5 py-2 bg-[#FEDB1A] text-[#1E4289] font-semibold text-base rounded-full hover:bg-white transition-colors"
            >
              <BsBoxArrowInRight className="text-lg" />
              <span className="hidden sm:inline">Sign In</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
